import { Cell, Row, Table } from "@tanstack/react-table"
import { TableColumnAlign, TableRowData, TableRowSelectVisibility, TableSize } from "../types"
import { Button } from "../../Button/Button"
import { Minus, Plus } from "@figurepos/fgr-lib-business-logic/lib/constants/symbols"
import React from "react"
import { classNames } from "../../../../utils/classNames"
import { Skeleton } from "../../Skeleton/Skeleton"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { Checkbox } from "antd"
import { head } from "ramda"
import { flexRenderCustom, getCellHeightClasses, getFirstColButtonsWidth } from "../styleUtils"

type Props<TRowData> = {
    renderSkeletons: boolean
    virtualized?: boolean
    size: TableSize
    table: Table<TRowData>
    row: Row<TRowData>
    cell: Cell<TRowData, any>
}

export const TableBodyCell = <TRowData extends TableRowData>(props: Props<TRowData>) => {
    const isFirstColumn = head(props.table.getAllColumns())?.id === props.cell.column.id

    const isExpandButtonCol = !!(isFirstColumn && props.table.options.meta?.hasExpandableRows)
    const isSelectCheckboxCol = !!(isFirstColumn && props.table.options.meta?.hasSelectableRows)

    const hasExpandButton = props.row.getCanExpand() && isExpandButtonCol
    const hasSelectCheckbox = props.row.original.isSelectable && isSelectCheckboxCol

    const firstColButtonsWidth = getFirstColButtonsWidth(isExpandButtonCol, isSelectCheckboxCol, props.row.depth)

    return (
        <TdComponent {...props}>
            {isFirstColumn && firstColButtonsWidth > 0 ? (
                <div
                    className={classes.firstColumnLeftPadding}
                    style={{
                        width: firstColButtonsWidth + "px",
                    }}>
                    {hasSelectCheckbox ? (
                        <SelectCheckbox
                            row={props.row}
                            mode={props.table.options.meta?.rowSelectVisibility || TableRowSelectVisibility.Always}
                            isAnySelected={props.table.getIsSomeRowsSelected() || props.table.getIsAllRowsSelected()}
                        />
                    ) : null}
                    {hasExpandButton ? <ExpandBtn row={props.row} /> : null}
                </div>
            ) : null}
            {flexRenderCustom(props.cell.column.columnDef.cell, props.cell.getContext())}
        </TdComponent>
    )
}

const TdComponent = <TRowData extends TableRowData>(
    props: React.PropsWithChildren<Props<TRowData>>
): React.ReactElement => {
    const alignClass = columnAlignToClass(props.cell.column.columnDef.meta?.align)

    const renderContent = () => {
        if (props.renderSkeletons) {
            return <Skeleton />
        }
        if (props.virtualized) {
            return props.children
        }
        // Todo: we probably want to refactor whole table to use "grid" and "flex" elements
        // not just when virtualization is enabled, but always (so we can avoid such conditions).
        return <div className={classNames(classes.wrap, alignClass)}>{props.children}</div>
    }

    return (
        <td
            style={{
                width: props.cell.column.columnDef.meta?.widthPercentage ?? props.cell.column.getSize(),
                maxWidth: props.cell.column.columnDef.meta?.maxWidth,
            }}
            className={classNames(
                classes.bodyCell,
                getCellHeightClasses(props.size),
                props.virtualized && [classes.bodyCellVirtualized, alignClass],
                props.cell.column.columnDef.meta?.columnClassName
            )}>
            {renderContent()}
        </td>
    )
}

const ExpandBtn = <TRowData extends TableRowData>({ row }: { row: Row<TRowData> }) => (
    <Button
        className={classes.expandBtn}
        onClick={e => {
            e.stopPropagation()
            row.getToggleExpandedHandler()()
        }}>
        {row.getIsExpanded() ? Minus : Plus}
    </Button>
)

const SelectCheckbox = <TRowData extends TableRowData>({
    row,
    mode,
    isAnySelected,
}: {
    row: Row<TRowData>
    mode: TableRowSelectVisibility
    isAnySelected: boolean
}) => {
    const visibilityClass =
        mode === TableRowSelectVisibility.Always || isAnySelected ? "" : "hidden group-hover/table-row:block"
    return (
        <Checkbox
            className={classNames(classes.selectCheckboxBtn, visibilityClass)}
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onClick={e => e.stopPropagation()}
            onChange={row.getToggleSelectedHandler()}
        />
    )
}

const columnAlignToClass = (align: Maybe<TableColumnAlign>): string => {
    switch (align) {
        case "left":
            return "justify-start text-left"
        case "center":
            return "justify-center text-center"
        case "right":
            return "justify-end text-right"
        default:
            return "justify-start text-left"
    }
}

const classes = {
    bodyCell: "text-black/80 text-14",
    bodyCellVirtualized: "relative flex items-center",
    wrap: "relative flex items-center",
    firstColumnLeftPadding: "flex shrink-0 h-full items-center pr-3",
    expandBtn: "ml-auto h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-sm p-0 pb-px leading-none",
    selectCheckboxBtn: "mr-auto",
}
